import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Hash, Tag, AtSign, Loader2, Radio } from "lucide-react";

const SENTIMENT_COLORS: Record<string, string> = {
  positive: "bg-emerald-100 text-emerald-700",
  neutral:  "bg-slate-100 text-slate-600",
  negative: "bg-red-100 text-red-700",
};

const TYPE_ICONS: Record<string, any> = {
  keyword: Hash,
  hashtag: Tag,
  mention: AtSign,
};

export function ListeningDashboardV2() {
  const { orgId } = useOrgPermissions();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [newTopic, setNewTopic] = useState("");
  const [newType, setNewType] = useState("keyword");
  const [filterSentiment, setFilterSentiment] = useState("all");

  const { data: topics = [], isLoading } = useQuery({
    queryKey: ["listening-topics", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from("listening_topics" as any)
        .select("*")
        .eq("org_id", orgId!)
        .order("count", { ascending: false }) as any);
      return (data ?? []) as any[];
    },
  });

  const filtered = filterSentiment === "all"
    ? topics
    : topics.filter((t: any) => t.sentiment === filterSentiment);

  const totalMentions = topics.reduce((acc: number, t: any) => acc + (t.count ?? 0), 0);
  const positive = topics.filter((t: any) => t.sentiment === "positive").length;
  const negative = topics.filter((t: any) => t.sentiment === "negative").length;

  const addMutation = useMutation({
    mutationFn: async () => {
      if (!newTopic.trim()) throw new Error("Enter a topic");
      const today = new Date().toISOString().split("T")[0];
      const { error } = await (supabase
        .from("listening_topics" as any)
        .insert({
          org_id:       orgId,
          topic:        newTopic.trim(),
          topic_type:   newType,
          count:        0,
          period_start: today,
          period_end:   today,
        }) as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Topic added." });
      setNewTopic("");
      qc.invalidateQueries({ queryKey: ["listening-topics", orgId] });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase
        .from("listening_topics" as any)
        .delete()
        .eq("id", id) as any);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["listening-topics", orgId] }),
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  return (
    <div className="space-y-5">
      {/* Stat row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Topics tracked",    value: topics.length,  cls: "text-[#0D2240]" },
          { label: "Total mentions",    value: totalMentions,  cls: "text-[#006B3C]" },
          { label: "Positive topics",   value: positive,       cls: "text-emerald-600" },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <div className={`text-2xl font-bold ${s.cls}`}>{s.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add topic */}
      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Radio className="w-4 h-4" /> Add Listening Topic</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="e.g. parliament, #budget2026, @conorbit"
              value={newTopic}
              onChange={e => setNewTopic(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addMutation.mutate()}
              className="flex-1"
            />
            <Select value={newType} onValueChange={setNewType}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="keyword">Keyword</SelectItem>
                <SelectItem value="hashtag">Hashtag</SelectItem>
                <SelectItem value="mention">Mention</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => addMutation.mutate()} disabled={addMutation.isPending || !newTopic.trim()}>
              {addMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Label className="text-xs">Sentiment:</Label>
        {["all", "positive", "neutral", "negative"].map(s => (
          <button
            key={s}
            onClick={() => setFilterSentiment(s)}
            className={`px-2.5 py-1 rounded-full text-xs capitalize font-medium border transition-colors ${
              filterSentiment === s
                ? "bg-[#0D2240] text-white border-transparent"
                : "border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>

      {/* Topic list */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading topics…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No topics yet. Add your first listening topic above.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((topic: any) => {
            const TypeIcon = TYPE_ICONS[topic.topic_type ?? "keyword"] ?? Hash;
            return (
              <Card key={topic.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#0D2240]/10 flex items-center justify-center shrink-0">
                    <TypeIcon className="w-4 h-4 text-[#0D2240]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{topic.topic}</p>
                    <p className="text-xs text-muted-foreground">
                      {topic.count ?? 0} mentions · {new Date(topic.period_start).toLocaleDateString("en-GB", { month: "short", day: "numeric" })} – {new Date(topic.period_end).toLocaleDateString("en-GB", { month: "short", day: "numeric" })}
                    </p>
                  </div>
                  {topic.sentiment && (
                    <Badge className={`text-[10px] ${SENTIMENT_COLORS[topic.sentiment] ?? ""}`}>
                      {topic.sentiment}
                    </Badge>
                  )}
                  <div className="font-bold text-sm w-10 text-right">{topic.count ?? 0}</div>
                  <button
                    onClick={() => window.confirm(`Stop tracking "${topic.topic}"? Its accumulated mention history will be deleted.`) && deleteMutation.mutate(topic.id)}
                    disabled={deleteMutation.isPending}
                    className="p-1 text-muted-foreground hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
